import { createContext, useCallback, useContext, useState } from "react";
import { Alert, Slide, Snackbar } from "@mui/material";

const SlideDown = (props) => <Slide {...props} direction="down" />;

const AlertContext = createContext({});

export const AlertProvider = ({ children }) => {
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "info", // "success" | "error" | "warning" | "info"
    duration: 4000,
  });

  const showAlert = useCallback((message, severity = "info", duration = 4000) => {
    setAlert({ open: true, message, severity, duration });
  }, []);

  const showSuccess = useCallback(
    (message, duration) => showAlert(message, "success", duration),
    [showAlert]
  );

  const showError = useCallback(
    (message, duration) => showAlert(message, "error", duration ?? 6000),
    [showAlert]
  );

  const showWarning = useCallback(
    (message, duration) => showAlert(message, "warning", duration),
    [showAlert]
  );

  const hide = useCallback(() => {
    setAlert((prev) => ({ ...prev, open: false }));
  }, []);

  return (
    <AlertContext.Provider
      value={{ showSuccess, showError, showWarning, showAlert, hide }}
    >
      {children}
      <Snackbar
        open={alert.open}
        autoHideDuration={alert.duration}
        onClose={hide}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        TransitionComponent={SlideDown}
      >
        <Alert
          onClose={hide}
          severity={alert.severity}
          variant="filled"
          sx={{
            width: "100%",
            color: "#fff",
            fontWeight: 600,
            "& .MuiAlert-icon": { color: "#fff" },
            "& .MuiAlert-action svg": { color: "#fff" },
          }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </AlertContext.Provider>
  );
};

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error("useAlert must be used within AlertProvider");
  }
  return context;
};

export default AlertContext;
